import { createQuery, Not, type World } from 'koota';
import { emit, getContext } from '../context';
import { spawnRagdoll } from '../player/ragdoll';
import { destroyBody } from '../physics/bodies';
import {
  Combat,
  Controller,
  Dead,
  Health,
  Player,
  RoundPhase,
  RoundState,
  Status,
  Transform,
} from '../traits';
import { suddenDeathState } from '../rules/suddenDeath';
import { recordPartyDeath } from '../rules/party';
import { creditArmsRaceRingOut, markArmsRaceHit } from '../rules/armsRace';

const living = createQuery(Player, Health, Transform, Not(Dead));

export function damageDeath(world: World): void {
  const ctx = getContext(world);
  const bounds = ctx.level.bounds;
  const margin = ctx.level.killMargin ?? 6;
  const round = world.get(RoundState);
  const danger =
    round?.phase === RoundPhase.Fighting &&
    ctx.players.length > 1 &&
    ctx.settings.mode !== 'king-of-hill' &&
    ctx.settings.mode !== 'arms-race'
      ? suddenDeathState(bounds, round.ticks, ctx.settings.roundTimeLimit, ctx.tuning.tickRate)
      : null;

  world.query(living).updateEach(([player, health, transform], entity) => {
    // Deaths dealt by takeDamage() already emitted their kill; burn ticks and the void below do not.
    const aliveBefore = health.hp > 0;
    let cause: 'void' | 'burn' | 'sudden-death' | undefined;
    const status = entity.get(Status);
    if (status) {
      if (status.burning > 0) {
        status.burning -= 1;
        if (ctx.tick % 12 === 0) {
          markArmsRaceHit(world, entity, -1);
          health.hp -= 5;
          cause = 'burn';
        }
      }
      if (status.slowed > 0) status.slowed -= 1;
      if (status.glued > 0) status.glued -= 1;
      if (status.bubbled > 0) status.bubbled -= 1;
      if (status.pulled > 0) status.pulled -= 1;
      entity.set(Status, status);
    }

    const oob =
      transform.x < bounds.x - margin ||
      transform.x > bounds.x + bounds.w + margin ||
      transform.y < bounds.y - margin ||
      transform.y > bounds.y + bounds.h + margin;
    if (
      danger?.active &&
      (transform.x < danger.left || transform.x > danger.right || danger.progress === 1) &&
      ctx.tick % 6 === 0
    ) {
      health.hp -= 3;
      cause = 'sudden-death';
    }
    if (oob) {
      health.hp = 0;
      cause = 'void';
    }

    if (health.hp <= 0 && aliveBefore) {
      emit(world, {
        type: 'kill',
        source: cause === 'void' ? creditArmsRaceRingOut(world, entity) : -1,
        target: entity,
        x: transform.x,
        y: transform.y,
        cause,
      });
    }

    if (health.hp <= 0) {
      entity.add(Dead());
      const ctrl = entity.get(Controller);
      spawnRagdoll(world, entity, ctrl?.vx ?? 0, ctrl?.vy ?? 0);
      destroyBody(world, entity);
      entity.remove(Combat);
      recordPartyDeath(world, entity);
      emit(world, { type: 'blood', x: transform.x, y: transform.y, amount: 40 });
      void player;
    }
  });
}
